import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { commitMutation, type FetchPolicy, graphql } from "react-relay";

import { faCalendarPlus, faCommentAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";
import { commitLocalUpdate } from "relay-runtime";

import AppContext from "~/common/AppContext";
import type { AssigneeType, UserType } from "~/common/AssigneeFilter";
import { useFeature } from "~/lib/flippy";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import WebsocketClient from "~/lib/websocket";
import { environment } from "~/models";
import Filters, { Source } from "~/okrs/components/Filters";
import CreateObjective from "~/okrs/CreateObjective";
import { eventEmitter as okrEventEmitter } from "~/okrs/events";
import ObjectiveQuery from "~/okrs/ObjectiveQuery";
import { staleObjectiveRecords } from "~/okrs/utils";
import { TeamContext } from "~/team";
import { Button } from "~/ui";
import { Page } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import { __globalModalContext } from "~/ui/modals";

import PaidFeatureInfoBlocks from "../billing/PaidFeatureInfoBlocks";
import PaidFeatureInPage from "../billing/PaidFeatureInPage";
import GettingStarted from "./components/GettingStarted";

import type { ObjectiveListQuery } from "~/__generated__/ObjectiveListQuery.graphql";
import type { ObjectiveListSetOkrOnboardingSeenOrSkippedMutation as ObjectiveListSetOkrOnboardingSeenOrSkippedMutationType } from "~/__generated__/ObjectiveListSetOkrOnboardingSeenOrSkippedMutation.graphql";
import type { DisplayType } from "~/__generated__/ObjectiveQueryQuery.graphql";
import type { ObjectiveStatus } from "~/__generated__/ObjectiveQueryViewer.graphql";
import type { teamQuery_Info$data as Team } from "~/__generated__/teamQuery_Info.graphql";

import styles from "./ObjectiveList.module.css";

const objectiveListQuery = graphql`
	query ObjectiveListQuery {
		viewer {
			hasSkippedOrSeenOkrsGettingStarted
			account {
				billingAccount {
					hasAccessToObjectives: hasAccessToFeature(feature: objectives)
				}
			}
		}
	}
`;

const setOkrOnboardingSeenOrSkipped = graphql`
	mutation ObjectiveListSetOkrOnboardingSeenOrSkippedMutation($input: UpdateOkrsOnboardingSeenInput!) {
		setOkrOnboardingSeenOrSkipped(input: $input) {
			ok
		}
	}
`;

type FetchOptions = {
	fetchKey: number;
	fetchPolicy: FetchPolicy | undefined;
};

type Filter = {
	view: DisplayType;
	onChangeView(newView: DisplayType): void;
	filterName?: string;
	onChangeFilterName(newFilterName: string): void;
	filterAssignee?: AssigneeType[];
	onChangeFilterAssignee(newAssignee: AssigneeType[]): void;
	filterStatus?: ObjectiveStatus;
	onChangeFilterStatus(newFilterStatus?: ObjectiveStatus): () => void;
	initialFilterAssignees?: string[];
	filterCycles?: string[];
	onChangeFilterCycles(newFilterCycles: string[]): void;
};

type WebsocketMessage = {
	type: string;
	data: {
		keyResult?: {
			id: string;
			progress: number;
		};
		objectives: {
			id: string;
			progress: number;
		}[];
	};
};

export type OKRsContextType = {
	createView?: boolean;
	editView?: boolean;
	children?: boolean;
	filters?: Filter;
	fetchOptions?: FetchOptions;
	refetch?: (objectiveId?: string) => void;
	resetFetchPolicy?: () => void;
	openCreateObjectiveModal?: () => void;
	team?: Omit<Team, " $fragmentType">;
	setIsLoading?: React.Dispatch<React.SetStateAction<boolean | null>>;
};

export const OKRsContext = createContext<OKRsContextType>({
	createView: false,
	editView: false,
	children: false,
	fetchOptions: {
		fetchKey: 0,
		fetchPolicy: "store-and-network",
	},
	refetch: () => null,
	resetFetchPolicy: () => null,
	openCreateObjectiveModal: noop,
	setIsLoading: noop,
});

export type PresetFilterType = {
	assigneeUser?: UserType;
	assigneeIds?: string[];
	status?: ObjectiveStatus;
	cycles?: string[];
};

type ObjectiveListProps = {
	presetFilters?: PresetFilterType;
	fromSource?: string;
	source?: Source;
	className?: string;
};

export default function ObjectiveList({ fromSource, source, presetFilters, className }: ObjectiveListProps) {
	const [fetchKey, setFetchKey] = useState<number>(0);
	const [fetchPolicy, setFetchPolicy] = useState<FetchPolicy>("store-and-network");
	const [view, setView] = useState<DisplayType>("LIST");
	const [filterName, setFilterName] = useState<string>("");
	const [filterAssignee, setFilterAssignee] = useState<AssigneeType[]>(
		presetFilters?.assigneeUser ? [{ user: presetFilters.assigneeUser }] : [],
	);
	const [filterStatus, setFilterStatus] = useState<ObjectiveStatus | undefined>(presetFilters?.status);
	const [filterCycles, setFilterCycles] = useState<string[]>(presetFilters?.cycles || []);
	const [archivedCollapsed, setArchivedCollapsed] = useState<boolean>(true);
	const accountId = useContext(AppContext).account?.id;
	const { team, refetchId } = useContext(TeamContext);
	const data = useLazyLoadQuery<ObjectiveListQuery>(objectiveListQuery, {}, {});

	const { hasAccessToObjectives } = data.viewer.account.billingAccount;

	const hasSkippedOrSeenOkrsGettingStarted = data.viewer.hasSkippedOrSeenOkrsGettingStarted;
	const [hasSeenGettingStartedVideo, setHasSeenGettingStartedVideo] = useState(false);

	const okrsDisabled = useFeature("okrs-disabled", "account");

	useEffect(() => {
		setFilterStatus(presetFilters?.status);
		setFilterCycles(presetFilters?.cycles || []);
	}, [presetFilters]);

	const onWebsocketMessage = React.useCallback((data: WebsocketMessage) => {
		if (data.type !== "OBJECTIVES_UPDATED") {
			return;
		}

		const d = data.data;

		commitLocalUpdate(environment, store => {
			if (d.keyResult) {
				const keyResultNode = store.get(d.keyResult.id);
				if (keyResultNode) {
					keyResultNode.setValue(d.keyResult.progress, "progress");
				}
			}

			for (let objective of d.objectives) {
				const objectiveNode = store.get(objective.id);
				if (objectiveNode) {
					objectiveNode.setValue(objective.progress, "progress");
				}
			}
		});
	}, []);

	React.useEffect(() => {
		const key = `objectives.${accountId}`.replace(/=/g, "");
		const socket = WebsocketClient.getInstance().getOrJoinGroup(key, {});
		socket.on("message", onWebsocketMessage);

		return () => {
			socket.off("message", onWebsocketMessage);
		};
	}, [accountId, onWebsocketMessage]);

	const hasSeenVideo = () => {
		setHasSeenGettingStartedVideo(true);
	};

	const setSkippedOrSeen = useCallback(() => {
		commitMutation<ObjectiveListSetOkrOnboardingSeenOrSkippedMutationType>(environment, {
			mutation: setOkrOnboardingSeenOrSkipped,
			variables: {
				input: { hasSeen: true },
			},
			onCompleted: () => {
				hasSeenVideo();
			},
			updater(store) {
				const viewer = store.get("viewer");
				if (!viewer) return;

				const user = viewer.getLinkedRecord("user");
				if (!user) return;
				user.setValue(true, "hasSkippedOrSeenOkrsGettingStarted");
			},
		});
	}, []);

	const refetch = useCallback(
		(objectiveId?: string) => {
			if (objectiveId) {
				// We need to stale the objective records to guarantee a proper refetch.
				// For a detailed explanation to why and how we do this
				// go to the function definition.
				commitLocalUpdate(environment, store => {
					staleObjectiveRecords(store);
				});
			}
			setFetchKey(n => n + 1);
			// We could use "store-and-network" here maybe...
			setFetchPolicy("network-only");
		},
		[setFetchKey, setFetchPolicy],
	);

	// This is used to trigger refetchs from a parent/sibling component
	// Using context
	useEffect(() => {
		if (!refetchId) {
			return;
		}
		refetch();
	}, [refetchId, refetch]);

	useEffect(() => {
		okrEventEmitter.on("Okrs.refetch", refetch);

		return () => {
			okrEventEmitter.off("Okrs.refetch", refetch);
		};
	}, [refetch]);

	const resetFetchPolicy = useCallback(() => {
		setFetchPolicy("store-and-network");
	}, [setFetchPolicy]);

	const openCreateObjectiveModal = useCallback(() => {
		__globalModalContext?.open(({ close }) => <CreateObjective onClose={close} refetch={refetch} />);
	}, [refetch]);

	const toggleArchivedCollapsed = () => {
		setArchivedCollapsed(!archivedCollapsed);
	};

	const onChangeView = useCallback(
		(newView: DisplayType) => {
			setView(newView);
		},
		[setView],
	);

	const onChangeFilterName = useCallback(
		(newFilterName: string) => {
			setFilterName(newFilterName);
		},
		[setFilterName],
	);

	const onChangeFilterStatus = useCallback(
		(newFilterStatus?: ObjectiveStatus) => () => {
			setFilterStatus(newFilterStatus);
		},
		[setFilterStatus],
	);

	const onChangeFilterCycles = useCallback(
		(newFilterCycles: string[]) => {
			setFilterCycles(newFilterCycles);
		},
		[setFilterCycles],
	);

	const contextValues = useMemo<{
		createView: boolean;
		editView: boolean;
		filters: Filter;
		fetchOptions: FetchOptions;
		refetch: () => void;
		resetFetchPolicy: () => void;
		team?: Omit<Team, " $fragmentType">;
	}>(() => {
		return {
			createView: false,
			editView: false,
			filters: {
				view,
				onChangeView,
				filterName,
				onChangeFilterName,
				filterAssignee,
				onChangeFilterAssignee: setFilterAssignee,
				filterStatus,
				onChangeFilterStatus,
				initialFilterAssignees: presetFilters?.assigneeIds || [],
				filterCycles,
				onChangeFilterCycles,
			},
			fetchOptions: { fetchKey, fetchPolicy },
			refetch,
			resetFetchPolicy,
			openCreateObjectiveModal,
			team,
		};
	}, [
		view,
		onChangeView,
		filterName,
		onChangeFilterName,
		filterAssignee,
		setFilterAssignee,
		filterStatus,
		onChangeFilterStatus,
		presetFilters,
		filterCycles,
		onChangeFilterCycles,
		fetchKey,
		fetchPolicy,
		refetch,
		resetFetchPolicy,
		openCreateObjectiveModal,
		team,
	]);

	if (okrsDisabled) {
		return null;
	}

	if (!hasAccessToObjectives) {
		return (
			<div style={{ display: "flex", height: "100%", justifyContent: "center", alignItems: "center" }}>
				<PaidFeatureInPage id="objectives">
					<PaidFeatureInfoBlocks
						items={[
							{
								icon: <FontAwesomeIcon icon={faCalendarPlus} />,
								color: "peach",
								title: "Incorporate objectives into meetings",
								subtitle: "Stay on top of your goals by adding them to your team and 1-on-1 meeting agendas.",
							},
							{
								icon: <FontAwesomeIcon icon={faCommentAlt} />,
								color: "royal",
								title: "Keep your team accountable to their objectives",
								subtitle: "Share progress asynchronously with updates and comments.",
							},
						]}
					/>
				</PaidFeatureInPage>
			</div>
		);
	}

	if (hasSkippedOrSeenOkrsGettingStarted === false) {
		if (!hasSeenGettingStartedVideo) {
			return <GettingStarted completeOnboarding={setSkippedOrSeen} />;
		}
	}

	return (
		<Page className={cx(className, { [styles.fromSource]: fromSource })}>
			{!fromSource && (
				<Page.Header>
					<Page.Title>Objectives</Page.Title>
					<Page.HeaderActions>
						<Button variant="primary" size="normal" onClick={openCreateObjectiveModal}>
							Create objective
						</Button>
					</Page.HeaderActions>
				</Page.Header>
			)}
			<Page.Body>
				<OKRsContext.Provider value={contextValues}>
					<Filters fromSource={fromSource} source={source} />
					<React.Suspense fallback={<LoadingSpinner inline className={styles.loading} />}>
						<ObjectiveQuery />
					</React.Suspense>

					<div className={styles.archivedList}>
						<button
							className={cx(styles.collapseButton, styles.archivedCollapseButton, {
								[styles.collapseButtonCollapsed]: archivedCollapsed,
							})}
							onClick={toggleArchivedCollapsed}
						>
							<FontAwesomeIcon icon={faCaretDown} className={styles.collapseButtonCaret} />
							Archived
						</button>
						<div className={cx(styles.listItems, { [styles.archivedCollapsed]: archivedCollapsed })}>
							{!archivedCollapsed && (
								<React.Suspense fallback={<LoadingSpinner inline />}>
									<ObjectiveQuery archived />
								</React.Suspense>
							)}
						</div>
					</div>
				</OKRsContext.Provider>
			</Page.Body>
		</Page>
	);
}
